import React, { useEffect, useMemo, useState } from "react";
import { Button, Form, Modal, Spinner, Table } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, SlidersHorizontal } from "lucide-react";
import Axios, { authHeaders } from "../../utils/axios";
import { useToast } from "../../context/TostContext";
import CustomSelect from "../../components/form/CustomSelect";
import { formatDate } from "./shared";

/**
 * Import profiles: our own reusable settings for a kind of sheet (layer rules,
 * zone legend, label sizes, review thresholds). A profile is used by single
 * and bulk imports instead of each sheet's guesses or a copied import.
 */
export default function ProfileList() {
  const navigate = useNavigate();
  const toast = useToast();
  const [profiles, setProfiles] = useState(null);
  const [imports, setImports] = useState([]);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", authority: "", from_import_id: "" });

  useEffect(() => {
    Axios.get("/maps/gis-imports/profiles", authHeaders())
      .then((res) => setProfiles(res.data?.data || []))
      .catch(() => setProfiles([]));
    Axios.get("/maps/gis-imports", { params: { limit: 100, include_batches: true, with_analysis: true }, ...authHeaders() })
      .then((res) => setImports(res.data?.data?.data || []))
      .catch(() => {});
  }, []);

  const importOptions = useMemo(
    () =>
      imports
        .filter((i) => !form.authority || i.authority === form.authority)
        .map((i) => ({
          value: i.id,
          label: `${i.map?.title} · ${i.plan_name || ""} (${i.authority || "-"}) · ${i.status}`,
          authority: i.authority,
        })),
    [imports, form.authority]
  );

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await Axios.post(
        "/maps/gis-imports/profiles",
        {
          name: form.name.trim(),
          authority: form.authority.trim() || undefined,
          from_import_id: form.from_import_id || undefined,
        },
        authHeaders()
      );
      navigate(`/gis-imports/profiles/${res.data?.data?.id}`);
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not create the profile");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="bg-white rounded-2 common-shadow mt-4 p-4">
        <Button variant="link" className="p-0 d-flex align-items-center gap-1 mb-3" onClick={() => navigate("/gis-imports")}>
          <ArrowLeft size={16} /> All GIS imports
        </Button>
        <div className="d-flex flex-wrap align-items-start justify-content-between gap-3">
          <div style={{ maxWidth: 720 }}>
            <h5 className="fw-semibold mb-1 d-flex align-items-center gap-2">
              <SlidersHorizontal size={18} /> Import profiles
            </h5>
            <p className="text-muted mb-0">
              Your own settings for one kind of sheet — which PDF layers are zoning, parcels, villages, TP plots and
              roads; which colour is which zone; the text sizes of labels. Write a profile once (or save one from an
              import you have checked) and use it for every sheet of that kind.
            </p>
          </div>
          <Button className="d-flex align-items-center gap-2" onClick={() => setCreating(true)}>
            <Plus size={16} /> New profile
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-2 common-shadow mt-4 p-4">
        {profiles === null ? (
          <Spinner size="sm" />
        ) : profiles.length === 0 ? (
          <p className="text-muted mb-0">No profiles yet.</p>
        ) : (
          <Table hover responsive size="sm" className="mb-0 align-middle">
            <thead>
              <tr>
                <th>Profile</th>
                <th>Authority</th>
                <th className="text-end">Layer rules</th>
                <th className="text-end">Zone colours</th>
                <th className="text-end">Imports</th>
                <th>Version</th>
                <th>Updated</th>
              </tr>
            </thead>
            <tbody>
              {profiles.map((p) => (
                <tr key={p.id} role="button" style={{ cursor: "pointer" }} onClick={() => navigate(`/gis-imports/profiles/${p.id}`)}>
                  <td className="fw-medium">{p.name}</td>
                  <td>{p.authority}</td>
                  <td className="text-end">{p.settings?.layer_rules?.length ?? 0}</td>
                  <td className="text-end">{p.settings?.legend?.length ?? 0}</td>
                  <td className="text-end">{p.imports ?? 0}</td>
                  <td>v{p.version}</td>
                  <td className="small text-muted">{formatDate(p.updated_at)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </div>

      <Modal show={creating} onHide={() => setCreating(false)} centered>
        <Form onSubmit={create}>
          <Modal.Header closeButton>
            <Modal.Title className="fs-6">New import profile</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label className="small fw-medium">Name</Form.Label>
              <Form.Control
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="e.g. GIFT City DP 2024"
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="small fw-medium">Authority</Form.Label>
              <Form.Control
                value={form.authority}
                maxLength={30}
                onChange={(e) => setForm((f) => ({ ...f, authority: e.target.value.toUpperCase(), from_import_id: "" }))}
                placeholder="e.g. GUDA"
              />
            </Form.Group>
            <CustomSelect
              label={{ name: "Start from an import (optional)" }}
              options={importOptions}
              value={importOptions.find((o) => o.value === form.from_import_id) || null}
              onChange={(o) =>
                setForm((f) => ({ ...f, from_import_id: o?.value || "", authority: f.authority || o?.authority || "" }))
              }
              placeholder="Its current settings become the profile's"
              isClearable
            />
            <Form.Text muted>Without one, the profile starts empty and you add the rules yourself.</Form.Text>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="light" onClick={() => setCreating(false)}>Cancel</Button>
            <Button type="submit" disabled={saving || !form.name.trim() || (!form.authority.trim() && !form.from_import_id)}>
              {saving ? <Spinner size="sm" /> : "Create"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
}
